import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Star, Quote } from "lucide-react";
import { fetchReviews } from "../../lib/api";
import { Reveal } from "./Reveal";

const StarRow = ({ rating = 5 }) => (
  <div className="flex gap-1">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={15} className={i < Math.round(rating) ? "text-hb-brass fill-hb-brass" : "text-hb-line"} />
    ))}
  </div>
);

export const GoogleReviews = () => {
  const { data } = useQuery({ queryKey: ["reviews"], queryFn: fetchReviews });
  const reviews = data?.reviews || [];
  const hasReviews = data?.configured && reviews.length > 0;

  return (
    <section className="bg-hb-surface py-24 md:py-32" data-testid="social-proof">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div className="text-center mb-14">
          <Reveal><p className="eyebrow text-hb-brass mb-5">Folks Are Talking</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-serif text-4xl md:text-6xl text-hb-cream leading-none">Word Around Town</h2></Reveal>
          {hasReviews && (data?.rating || data?.user_ratings_total) && (
            <Reveal delay={0.1}>
              <div className="flex items-center justify-center gap-3 mt-6">
                <StarRow rating={data.rating || 5} />
                <span className="text-hb-cream font-serif text-2xl">{data.rating ? data.rating.toFixed(1) : ""}</span>
                {data.user_ratings_total ? <span className="text-hb-muted text-sm">· {data.user_ratings_total} Google reviews</span> : null}
              </div>
            </Reveal>
          )}
        </div>

        {hasReviews ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.slice(0, 6).map((r, i) => (
                <Reveal key={r.id || i} delay={(i % 3) * 0.06}>
                  <div className="border border-hb-line bg-hb-black/40 p-7 h-full flex flex-col" data-testid="review-card">
                    <Quote className="text-hb-brass/40 mb-4" size={26} />
                    <StarRow rating={r.rating || 5} />
                    <p className="text-hb-cream/90 text-sm leading-relaxed mt-4 flex-1">{r.text}</p>
                    <div className="mt-5 pt-4 border-t border-hb-line flex items-center gap-3">
                      {r.author_photo ? (
                        <img src={r.author_photo} alt={r.author} className="h-9 w-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-hb-elevated flex items-center justify-center font-serif text-hb-brass">{(r.author || "G")[0]}</div>
                      )}
                      <div>
                        <p className="text-hb-cream text-sm font-medium">{r.author}</p>
                        <p className="text-hb-mutedlight text-xs">{r.relative_time}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="text-center mt-10">
              <p className="eyebrow text-hb-mutedlight text-[0.6rem] mb-4">Reviews provided by Google · sorted by relevance</p>
              {data?.reviews_uri && (
                <a href={data.reviews_uri} target="_blank" rel="noreferrer" className="btn-outline-cream eyebrow px-8 py-4 inline-flex" data-testid="all-reviews-link">
                  See All Reviews on Google
                </a>
              )}
            </div>
          </>
        ) : (
          <Reveal delay={0.1}>
            <div className="border border-hb-line bg-hb-black/40 p-10 md:p-16 relative text-center max-w-3xl mx-auto" data-testid="reviews-placeholder">
              <Quote className="text-hb-brass/40 mx-auto mb-6" size={40} />
              <div className="flex justify-center gap-1 mb-6"><StarRow rating={5} /></div>
              <p className="font-serif text-2xl md:text-3xl text-hb-cream italic leading-snug">
                Real reviews from our Ben Wheeler neighbors will appear here automatically once our Google listing is live.
              </p>
              <p className="eyebrow text-hb-muted mt-8 text-[0.65rem]">Authentic Google Reviews · Coming Soon</p>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
